import classNames from "classnames";
import { useRef, useState } from "react";
import { BaseButton } from "../../../components/Button/BaseButton.tsx";
import { PimentoStar } from "../../../components/Icon/assets/PimentoStar.tsx";
import { Icon, type IconName } from "../../../components/Icon/Icon.tsx";
import { Popover } from "../../../components/Popover/Popover.tsx";
import { trackEvent } from "../../../tracking/tracking.ts";
import { NEW_BLANK_PROJECT_PRESET } from "../../boardState/constants.ts";
import type { StatePreset } from "../../boardState/types.ts";
import { useRedirectToBoard } from "../../hooks/useRedirectToBoard.ts";
import { QUICK_ACTIONS_CATEGORIES } from "../QuickActions/constants.ts";
import { CATEGORY_ICONS } from "../QuickActions/display.ts";
import { quickActionsDialogStore } from "../QuickActions/stores/quickActionsDialogStore.ts";
import { useCreateBoard } from "./hooks/useCreateBoard.ts";

export const QuickActionsSection = () => {
  const redirectToBoard = useRedirectToBoard();
  const boardCreationMode = useRef<StatePreset>();
  const { createBoard, isCreateBoardLoading } = useCreateBoard({
    onSuccess: (uuid) => {
      redirectToBoard({
        uuid,
        boardStatePreset: boardCreationMode.current ?? NEW_BLANK_PROJECT_PRESET,
      });
    },
  });

  return (
    <div className="relative overflow-clip rounded-400 bg-primary">
      <PimentoStar
        size={102}
        className="absolute top-[-18px] left-[-47px] fill-brand-blue"
      />
      <PimentoStar
        size={102}
        className="absolute bottom-[-34px] right-[-51px] fill-brand-blue"
      />
      <div className="flex-col items-center py-600 gap-400">
        <div className="flex-col-center gap-100">
          <div className="heading-2xl">What will you create today?</div>
          <div className="label-lg-default text-secondary">
            Discover the tools empowering creators like you
          </div>
        </div>
        <div className="flex-row gap-200">
          <ActionTileButton
            title="New project"
            iconName="Proportions"
            isLoading={isCreateBoardLoading}
            onClick={() => {
              trackEvent("quick_actions:select", {
                preset: "new_blank_project",
              });
              boardCreationMode.current = "new_blank_project";
              createBoard();
            }}
          />
          {QUICK_ACTIONS_CATEGORIES.map((category) => (
            <ActionTileButton
              key={category}
              title={category}
              iconName={CATEGORY_ICONS[category]}
              onClick={() => {
                trackEvent("quick_actions:open_dialog", {
                  origin: "quick_actions_section",
                  category,
                });
                quickActionsDialogStore.selectCategory(category);
                quickActionsDialogStore.open();
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const ActionTileButton = ({
  title,
  iconName,
  onClick,
  isLoading = false,
  comingSoon = false,
}: {
  title: string;
  iconName: IconName;
  onClick?: () => void;
  isLoading?: boolean;
  comingSoon?: boolean;
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  return (
    <div className="flex-col gap-400 items-center w-[88px]">
      <Popover
        isOpen={comingSoon && isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        sideOffset={6}
        content={
          <div className="px-400 py-200 body-md-semibold">Coming soon</div>
        }
      >
        <BaseButton
          onClick={() => {
            onClick?.();
          }}
          className={classNames(
            "group flex-col justify-center items-center w-1200 aspect-square p-200 rounded-400 border-025 border-primary-rest",
            "bg-surface-primary-rest hover:bg-surface-primary-hover",
          )}
          loading={isLoading}
          onMouseEnter={() => {
            comingSoon && setIsPopoverOpen(true);
          }}
          onMouseLeave={() => {
            comingSoon && setIsPopoverOpen(false);
          }}
        >
          <Icon
            size="lg"
            name={iconName}
            className="group-hover:text-primary-hover"
          />
        </BaseButton>
      </Popover>
      <span className="text-center label-md-default text-primary">{title}</span>
    </div>
  );
};
